import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import ContentCard from '../components/Common/ContentCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { Search, Film, MonitorPlay, Tv } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState('all');
  const [results, setResults] = useState({ movies: [], series: [], live: [] });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults({ movies: [], series: [], live: [] });
      return;
    }

    const doSearch = async () => {
      setIsLoading(true);
      try {
        const data = await xtreamApi.search(query);
        setResults({
          movies: data.movies || [],
          series: data.series || [],
          live: data.live || []
        });
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    doSearch();
  }, [query]);

  const totalResults = results.movies.length + results.series.length + results.live.length;

  const renderGrid = (items, type) => (
    <div className={`grid gap-4 ${type === 'live'
      ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10'
      : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'}`}>
      {items.map((item) => (
        <ContentCard key={item.stream_id || item.series_id} item={item} type={type} />
      ))}
    </div>
  );

  return (
    <div data-testid="search-page" className="min-h-screen bg-[#0a0a0b] pt-20 pb-12">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Search style={{color:"var(--accent)"}} className="w-8 h-8" />
            <h1 className="text-3xl font-bold text-white">Search Results</h1>
          </div>
          {query && !isLoading && (
            <p className="text-gray-400">{totalResults} results for "{query}"</p>
          )}
        </div>

        {!query && (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Enter a search term to find content</p>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-20">
            <LoadingSpinner text={`Searching for "${query}"...`} />
          </div>
        )}

        {query && !isLoading && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#1a1a1d] border border-gray-800 mb-8">
              <TabsTrigger value="all" className="">
                All ({totalResults})
              </TabsTrigger>
              <TabsTrigger value="movies" className="">
                <Film className="w-4 h-4 mr-2" />
                Movies ({results.movies.length})
              </TabsTrigger>
              <TabsTrigger value="series" className="">
                <MonitorPlay className="w-4 h-4 mr-2" />
                Series ({results.series.length})
              </TabsTrigger>
              <TabsTrigger value="live" className="">
                <Tv className="w-4 h-4 mr-2" />
                Live ({results.live.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-8">
              {results.movies.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Film style={{color:"var(--accent)"}} className="w-5 h-5" /> Movies
                  </h2>
                  {renderGrid(results.movies, 'movie')}
                </div>
              )}
              {results.series.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <MonitorPlay style={{color:"var(--accent)"}} className="w-5 h-5" /> Series
                  </h2>
                  {renderGrid(results.series, 'series')}
                </div>
              )}
              {results.live.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Tv style={{color:"var(--accent)"}} className="w-5 h-5" /> Live Channels
                  </h2>
                  {renderGrid(results.live, 'live')}
                </div>
              )}
              {totalResults === 0 && (
                <div className="text-center py-20">
                  <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No results found for "{query}"</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="movies">
              {results.movies.length > 0 ? renderGrid(results.movies, 'movie') : (
                <div className="text-center py-20"><Film className="w-16 h-16 text-gray-600 mx-auto mb-4" /><p className="text-gray-400">No movies found</p></div>
              )}
            </TabsContent>

            <TabsContent value="series">
              {results.series.length > 0 ? renderGrid(results.series, 'series') : (
                <div className="text-center py-20"><MonitorPlay className="w-16 h-16 text-gray-600 mx-auto mb-4" /><p className="text-gray-400">No series found</p></div>
              )}
            </TabsContent>

            <TabsContent value="live">
              {results.live.length > 0 ? renderGrid(results.live, 'live') : (
                <div className="text-center py-20"><Tv className="w-16 h-16 text-gray-600 mx-auto mb-4" /><p className="text-gray-400">No live channels found</p></div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
